/**
 * Command-column card for the client's per-platform profile copy (DD-043):
 * platform tabs, the active platform's rows, and Edit, which opens the
 * shared drawer (DD-051) for that platform. The one place the agency edits it.
 */

import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { Client, SocialPlatform, SocialProfile } from "@/types";
import { clientService } from "@/domains/Client/Client.service";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { EditIcon } from "@/components/icons";
import { SocialPlatformTabs } from "@/domains/Client/components/SocialPlatformTabs";
import { SocialProfileRows } from "@/domains/Client/components/SocialProfileRows";
import { SocialProfileDrawer } from "@/domains/Client/components/SocialProfileDrawer";
import { emptySocialProfiles, fieldsFor, withPlatform } from "@/domains/Client/socialProfiles";

const NO_PROFILE: SocialProfile = {};

export const HomeSocialProfiles: React.FC<{ client: Client }> = ({ client }) => {
  const queryClient = useQueryClient();
  const { setSelectedClient } = useAppStore();
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [editing, setEditing] = useState(false);

  const profile = client.socialProfiles?.[platform] ?? NO_PROFILE;

  const handleSave = async (draft: SocialProfile) => {
    const base = client.socialProfiles ?? emptySocialProfiles();
    const updated = await clientService.updateClient(client.id, {
      socialProfiles: withPlatform(base, platform, draft),
    });
    setSelectedClient(updated);
    queryClient.invalidateQueries({ queryKey: ["clients"] });
    setEditing(false);
  };

  return (
    <section className="p-4 rounded-2xl bg-white">
      <div className="flex items-center justify-between px-1 mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Social profiles</h3>
        <PanelHeaderAction
          label="Edit"
          ariaLabel="Edit social profile"
          icon={<EditIcon className="w-3.5 h-3.5" />}
          onClick={() => setEditing(true)}
        />
      </div>
      <div className="px-1 mb-2">
        <SocialPlatformTabs platform={platform} onChange={setPlatform} />
      </div>
      <div className="px-1">
        <SocialProfileRows platform={platform} profile={profile} stacked />
      </div>

      <SocialProfileDrawer
        isOpen={editing}
        platform={platform}
        profile={profile}
        fields={fieldsFor(platform)}
        websitePlaceholder={client.websiteUrl ?? undefined}
        onSave={handleSave}
        onClose={() => setEditing(false)}
      />
    </section>
  );
};
